import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Loader2, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Address input with Google-Places-style autocomplete. Uses our backend proxy
 * `GET /api/onboarding/address-suggest?q=...` (OpenStreetMap Nominatim under
 * the hood — no API key, no browser CORS issues, no vendor lock-in).
 *
 * Props:
 *   value       — current text
 *   onChange    — (formatted:string, meta?:{line1,city,state,postal,country,lat,lon}) => void
 *   placeholder
 *   testIdRoot  — data-testid prefix (default "address")
 */
export function AddressAutocomplete({ value, onChange, placeholder, testIdRoot = "address", onPicked, verified = false }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);
  const skipNextRef = useRef(false);

  useEffect(() => {
    if (skipNextRef.current) { skipNextRef.current = false; return; }
    if (!value || value.trim().length < 3) { setItems([]); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await axios.get(`${API}/api/onboarding/address-suggest`, {
          params: { q: value },
        });
        setItems(data?.items || []);
        setOpen(true);
        setHighlight(-1);
      } catch {
        setItems([]);
      } finally {
        setBusy(false);
      }
    }, 300); // debounce (Nominatim asks 1req/sec — 300ms leaves headroom)
    return () => clearTimeout(timerRef.current);
  }, [value]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (it) => {
    skipNextRef.current = true; // don't re-query the picked value
    onChange(it.formatted, it);
    onPicked?.(it);
    setOpen(false);
    setItems([]);
  };

  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(items[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Input
          data-testid={`${testIdRoot}-input`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={() => { if (items.length) setOpen(true); }}
          placeholder={placeholder || "Start typing your address…"}
          autoComplete="off"
          className={
            "mt-1 bg-white/[0.04] border-white/10 text-white pr-16 " +
            (verified ? "border-emerald-500/40 focus-visible:border-emerald-500/60" : "")
          }
        />
        {verified && !busy && (
          <span
            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5"
            data-testid={`${testIdRoot}-verified-badge`}
          >
            <CheckCircle2 className="h-3 w-3" /> Verified
          </span>
        )}
        {busy && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 animate-spin" data-testid={`${testIdRoot}-loading`} />
        )}
      </div>

      {open && items.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border border-white/10 bg-[#0a0a0b] shadow-lg overflow-hidden max-h-64 overflow-y-auto"
          data-testid={`${testIdRoot}-suggestions`}
        >
          {items.map((it, idx) => (
            <button
              type="button"
              key={it.id || idx}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              onMouseEnter={() => setHighlight(idx)}
              data-testid={`${testIdRoot}-suggestion-${idx}`}
              className={
                "w-full text-left px-3 py-2 flex items-start gap-2 border-b border-white/[0.03] last:border-b-0 " +
                (highlight === idx ? "bg-white/[0.06]" : "hover:bg-white/[0.04]")
              }
            >
              <MapPin className="h-3.5 w-3.5 text-orange-400 mt-1 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white truncate">{it.formatted || it.display}</div>
                {it.display && it.display !== it.formatted && (
                  <div className="text-[11px] text-zinc-500 truncate">{it.display}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Preserve the same shape for a Label + AddressAutocomplete pair so pages can
// drop a full form field in one line.
export function AddressField({ label = "Address", value, onChange, hint, testIdRoot = "reg-address", placeholder, onPicked, verified = false }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-zinc-400">{label}</Label>
      <AddressAutocomplete
        value={value}
        onChange={onChange}
        onPicked={onPicked}
        verified={verified}
        placeholder={placeholder}
        testIdRoot={testIdRoot}
      />
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}
